import React, { FunctionComponent, memo, useEffect } from 'react'
import styles from './GalleryMobile.module.sass'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import Button from '../../../../controls/Button/Button'
import ChevronLeftIcon from 'files/chevron/left.svg'
import ChevronRightIcon from 'files/chevron/right.svg'
import DownloadIcon from 'files/download.svg'
import DeleteIcon from 'files/delete.svg'
import CommentIcon from 'files/comment/comment.svg'
import CloseIcon from 'files/close.svg'
import Spacer from '../../../Spacer'
import SidePanel from '../../../SidePanel/SidePanel'
import { useBoolean } from '../../../../../utils/hooks'
import { FileData } from '../../../../../responses/files'
import { GalleryData } from '../../../../../redux/global/gallery/gallery-actions'
import { isNone } from 'fp-ts/Option'
import fileSize from 'filesize'
import GalleryPreview from '../GalleryPreview/GalleryPreview'
import { ROUTES } from '../../../../../http/routes'
import { urlWithParams } from '../../../../../http/utils'
import GalleryComments from '../GalleryComments/GalleryComments'
import GalleryHeader from '../GalleryHeader/GalleryHeader'
import Ellipsis from '../../../Ellipsis/Ellipsis'
import { GalleryNavigation } from '../../navigation'

interface Props {
    current: FileData | null,
    data: GalleryData,
    navigation: GalleryNavigation,
    onPrev: () => void,
    onNext: () => void,
    onClose: () => void,
    onDelete: () => void,
}

const width = window.innerWidth
const threshold = width / 2

const variants: Variants = {
    initial: (direction: number) => ({
        x: direction > 0 ? width : -width,
        zIndex: 0,
        opacity: 0,
    }),
    default: {
        x: 0,
        zIndex: 1,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction < 0 ? width : -width,
        zIndex: 0,
        opacity: 0,
    }),
}

const GalleryMobile: FunctionComponent<Props> = ({current, ...props}) => {
    const { state: isOpen, setFalse: close, setTrue: open } = useBoolean()

    useEffect(() => close(), [close, current])

    return <div className={styles.container}>
        <div className={styles.top}>
            <Ellipsis className={styles.name}>
                {current?.name}
                    <span className={styles.bottom}>
                    {fileSize(current?.size ?? 0)}
                </span>
            </Ellipsis>
            <Spacer/>
            <Button onClick={props.onClose}><CloseIcon/></Button>
        </div>
        <div className={styles.swiper}>
            <AnimatePresence initial={false} custom={props.navigation.direction}>
                {
                    current && <motion.div
                        key={current.id}
                        className={styles.file}
                        custom={props.navigation.direction}
                        variants={variants}
                        initial={'initial'}
                        animate={'default'}
                        exit={'exit'}
                        drag={'x'}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={1}
                        onDragEnd={(e, info) => {
                            const swipe = Math.abs(info.offset.x) * (info.velocity.x + 1)
                            if (swipe < -threshold) {
                                props.onNext()
                            } else if (swipe > threshold) {
                                props.onPrev()
                            }
                        }}
                    >
                        <GalleryPreview className={styles.image} current={current}/>
                    </motion.div>
                }
            </AnimatePresence>
        </div>
        <div className={styles.footer}>
            {/*todo intent dark решить с исключением*/}
            <Button disabled={isNone(props.navigation.prev)} onClick={props.onPrev}>
                <ChevronLeftIcon/>
            </Button>
            {/*todo intent dark решить с исключением*/}
            <Button disabled={isNone(props.navigation.next)} onClick={props.onNext}>
                <ChevronRightIcon/>
            </Button>
            <Spacer/>
            {
                current && <>
                    <a href={urlWithParams({ filename: current.filename })(ROUTES.FILE_ORIGINAL)}>
                        {/*todo intent dark решить с исключением*/}
                        <Button><DownloadIcon/></Button>
                    </a>
                    {/*todo intent dark решить с исключением*/}
                    <Button onClick={props.onDelete}><DeleteIcon/></Button>
                </>
            }
            <Spacer/>
            <div style={{width: 30}}/>
            {/*todo intent dark решить с исключением*/}
            <Button onClick={open}><CommentIcon/></Button>
        </div>
        <AnimatePresence>
        {
            isOpen &&
                <SidePanel
                    zIndex={400}
                    onClose={close}
                    header={<GalleryHeader className={styles.header} data={props.data}/>}
                >
                    <GalleryComments className={styles.comments} data={props.data}/>
                </SidePanel>
        }
        </AnimatePresence>
    </div>
}

export default memo(GalleryMobile)